/**
 * Mapea códigos internos de commands de Caja a mensaje friendly.
 * Preserva `code` para Sentry; nunca devolver el raw al usuario.
 * Paridad: `mapSaleVoidRemoteError` (Ventas / ADR-029).
 */

export type MappedCashCommandError = {
  code: string;
  userMessage: string;
  expected?: boolean;
};

const CASH_ERROR_MAP: Array<{
  match: RegExp;
  code: string;
  userMessage: string;
  expected?: boolean;
}> = [
  {
    match: /cash_session_already_open/i,
    code: 'cash_session_already_open',
    userMessage: 'Ya hay una caja abierta en esta sucursal. Actualizá la pantalla.',
    expected: true,
  },
  {
    match: /cash_session_already_closed|ya est[aá] cerrad/i,
    code: 'cash_session_already_closed',
    userMessage: 'Ese turno ya estaba cerrado. Actualizá la pantalla.',
    expected: true,
  },
  {
    match: /cash_session_not_open|cash_session_not_found/i,
    code: 'cash_session_not_open',
    userMessage: 'No hay un turno abierto. Refrescá e intentá de nuevo.',
    expected: true,
  },
  {
    match: /cash_session_closed/i,
    code: 'cash_session_closed',
    userMessage: 'Ese turno ya está cerrado. No se puede anular el egreso.',
    expected: true,
  },
  {
    match: /cash_outflows_disabled/i,
    code: 'cash_outflows_disabled',
    userMessage: 'Los egresos de caja no están disponibles en este momento.',
    expected: true,
  },
  {
    match: /amount_invalid|invalid_amount/i,
    code: 'amount_invalid',
    userMessage: 'Ingresá un monto válido mayor a cero.',
    expected: true,
  },
  {
    match: /note_required_for_other/i,
    code: 'note_required_for_other',
    userMessage: 'Para la categoría “Otro” tenés que escribir un motivo.',
    expected: true,
  },
  {
    match: /direction_invalid|category_invalid_for_direction/i,
    code: 'category_invalid_for_direction',
    userMessage: 'Esa categoría no corresponde al tipo de movimiento. Revisá la selección.',
    expected: true,
  },
  {
    match: /category_required/i,
    code: 'category_required',
    userMessage: 'Elegí una categoría para el movimiento.',
    expected: true,
  },
  {
    match: /cash_outflow_not_found/i,
    code: 'cash_outflow_not_found',
    userMessage: 'No encontré ese egreso. Refrescá la lista e intentá de nuevo.',
    expected: true,
  },
  {
    match: /module_permission_denied|permission|supervisor|pin|forbidden|denied|not.?allowed/i,
    code: 'cash_permission_denied',
    userMessage: 'No tenés permiso para esta acción de caja. Pedile el PIN a un supervisor.',
    expected: true,
  },
  {
    match: /supervisor_override_invalid|supervisor_pin/i,
    code: 'supervisor_pin_invalid',
    userMessage: 'El PIN de supervisor no es válido o ya se usó. Pedí uno nuevo.',
    expected: true,
  },
  {
    match: /operationid_required/i,
    code: 'operationId_required',
    userMessage: 'No se pudo completar. Reintentá en unos segundos.',
  },
  {
    match: /branchid_required/i,
    code: 'branchId_required',
    userMessage: 'Seleccioná una sucursal e intentá de nuevo.',
    expected: true,
  },
  {
    match: /Ya hay una caja abierta/i,
    code: 'cash_session_already_open',
    userMessage: 'Ya hay una caja abierta en esta sucursal. Actualizá la pantalla.',
    expected: true,
  },
  {
    match: /cash_state_unverified/i,
    code: 'cash_state_unverified',
    userMessage: 'No pudimos verificar el estado de la caja. Reintentá en unos segundos.',
    expected: true,
  },
  {
    match: /device_offline|offline_write_blocked|offline|sin conexi[oó]n|network request failed|failed to fetch|load failed/i,
    code: 'device_offline',
    userMessage: 'Sin conexión. Revisá tu internet e intentá de nuevo.',
    expected: true,
  },
];

export function mapCashCommandError(raw: string): MappedCashCommandError {
  const msg = (raw || '').trim();
  for (const row of CASH_ERROR_MAP) {
    if (row.match.test(msg)) {
      return { code: row.code, userMessage: row.userMessage, expected: row.expected };
    }
  }
  return {
    code: msg ? 'cash_command_unknown' : 'cash_command_empty',
    userMessage: 'No se pudo completar la operación de caja. Reintentá o pedile a soporte.',
  };
}

/** Extrae texto crudo de Error / string / unknown para mapear. */
export function rawCashErrorText(error: unknown): string {
  // Errores ya mapeados (cashCommandErrorFromRaw) re-mapean por `code` —
  // el mensaje friendly no necesariamente matchea el regex original.
  const code =
    error && typeof error === 'object' ? (error as { code?: unknown }).code : undefined;
  if (typeof code === 'string' && code && CASH_ERROR_MAP.some((row) => row.match.test(code))) {
    return code;
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message ?? '');
  }
  return String(error ?? '');
}

/** Crea Error con mensaje friendly + `code` preservado. */
export function cashCommandErrorFromRaw(raw: string): Error & { code: string; expected?: boolean } {
  const mapped = mapCashCommandError(raw);
  const err = new Error(mapped.userMessage) as Error & { code: string; expected?: boolean };
  err.code = mapped.code;
  if (mapped.expected) err.expected = true;
  return err;
}
